import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { BrandL, LabelM } from '@sorare/core/src/atoms/typography';
import { TextureBackground } from '@sorare/core/src/atoms/ui/TextureBackground';
import { useMutation } from '@sorare/core/src/hooks/graphql/useMutation';
import { useQueryString } from '@sorare/core/src/hooks/useQueryString';

import type {
  UnsubscribeFromEmailsMutation,
  UnsubscribeFromEmailsMutationVariables,
} from './__generated__/page.graphql.ts';

const Root = styled(Vertical)`
  position: relative;
  width: var(--100vw);
  height: var(--100vh);
  isolation: isolate;
  padding: var(--quadruple-unit);
`;
const StyledTextureBackground = styled(TextureBackground)`
  position: absolute;
  inset: 0;
  z-index: -1;
`;

const unsubscribeFromEmailsMutation = gql`
  mutation UnsubscribeFromEmailsMutation($input: unsubscribeFromEmailsInput!) {
    unsubscribeFromEmails(input: $input) {
      errors {
        message
        code
      }
    }
  }
` as TypedDocumentNode<
  UnsubscribeFromEmailsMutation,
  UnsubscribeFromEmailsMutationVariables
>;

const UnsubscribeEmail = () => {
  const token = useQueryString('token');
  const [unsubscribe, { loading, called }] = useMutation(
    unsubscribeFromEmailsMutation,
    {
      showErrorsWithSnackNotification: true,
    }
  );

  useEffect(() => {
    if (!loading && token && !called) {
      unsubscribe({
        variables: {
          input: { token },
        },
      });
    }
  }, [loading, called, unsubscribe, token]);

  if (called && !loading) {
    return (
      <Root center className="text-center" gap={2}>
        <StyledTextureBackground background="wave1" color="var(--c-blue-400)" />
        <Vertical center>
          <BrandL>
            <FormattedMessage defaultMessage="You've been unsubscribed" />
          </BrandL>
        </Vertical>
        <LabelM color="var(--c-nd-600)" bold>
          <FormattedMessage defaultMessage="You will no longer receive marketing emails from Sorare. You can re-subscribe anytime from your notification settings." />
        </LabelM>
      </Root>
    );
  }
  return <LoadingIndicator fullScreen />;
};

export default UnsubscribeEmail;
